import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';
import { getTradingOffers } from '../redux/tradingOfferActions';
import {
  TradingOffer,
  TradingOfferStatusCounts,
  TradingOfferType,
} from '../../../types/TradingOffer';

type UseGetTradingOffersProps = {
  societyId?: string;
  page?: number;
  rowsPerPage?: number;
  tradingOfferType?: TradingOfferType;
  refetchKey?: number;
};

export const useGetTradingOffers = ({
  societyId,
  page = 1,
  rowsPerPage = 10,
  tradingOfferType,
  refetchKey = 0,
}: UseGetTradingOffersProps) => {
  const [tradingOffers, setTradingOffers] = useState<TradingOffer[]>([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<TradingOfferStatusCounts>();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchTradingOffers = useCallback(
    async (controller: AbortController) => {
      if (!societyId) return;

      try {
        setIsLoading(true);

        const {
          data,
          total: totalResult,
          statusCounts: statusCountsResult,
        } = await store.dispatch(
          getTradingOffers({
            societyId,
            params: {
              page,
              items: rowsPerPage,
              tradingOfferType,
            },
            signal: controller.signal,
          })
        );

        if (!controller.signal.aborted) {
          setTradingOffers(data);
          setTotal(totalResult);
          setStatusCounts(statusCountsResult);
          setIsLoading(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsError(true);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, rowsPerPage, societyId, refetchKey, tradingOfferType]
  );

  useEffect(() => {
    const controller = new AbortController();

    fetchTradingOffers(controller);

    return () => controller.abort();
  }, [fetchTradingOffers]);

  return {
    data: { tradingOffers, total, statusCounts },
    isLoading,
    isError,
  };
};
